import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  X,
  Plus,
  Tag,
  Package,
  ExternalLink,
  Upload,
  Building
} from 'lucide-react'
import { useLibraryStore, CustomEquipment } from '@/stores/libraryStore'
import { Equipment } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const equipmentSchema = z.object({
  name: z.string().min(1, 'Equipment name is required'),
  type: z.enum([
    'bodyweight', 'dumbbells', 'barbell', 'kettlebell', 'resistance-bands',
    'pull-up-bar', 'bench', 'cable-machine', 'treadmill', 'bike', 'rowing-machine',
    'yoga-mat', 'foam-roller', 'medicine-ball', 'suspension-trainer'
  ]),
  description: z.string().optional(),
  brand: z.string().optional(),
  model: z.string().optional(),
  purchaseUrl: z.string().url().optional().or(z.literal('')),
  imageUrl: z.string().url().optional().or(z.literal('')),
  tags: z.array(z.string()).optional()
})

type EquipmentFormData = z.infer<typeof equipmentSchema>

export function CreateEquipmentModal() {
  const {
    isCreateEquipmentModalOpen,
    setIsCreateEquipmentModalOpen,
    addEquipment
  } = useLibraryStore()

  const [saving, setSaving] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [newTag, setNewTag] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<EquipmentFormData>({
    resolver: zodResolver(equipmentSchema),
    defaultValues: {
      type: 'dumbbells',
      tags: []
    }
  })

  const equipmentTypeOptions: Equipment[] = [
    'bodyweight', 'dumbbells', 'barbell', 'kettlebell', 'resistance-bands',
    'pull-up-bar', 'bench', 'cable-machine', 'treadmill', 'bike', 'rowing-machine',
    'yoga-mat', 'foam-roller', 'medicine-ball', 'suspension-trainer'
  ]

  const handleClose = () => {
    setIsCreateEquipmentModalOpen(false)
    reset()
    setTags([])
    setNewTag('')
  }

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      const updatedTags = [...tags, newTag.trim()]
      setTags(updatedTags)
      setValue('tags', updatedTags)
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue('tags', updatedTags)
  }

  const onSubmit = async (data: EquipmentFormData) => {
    setSaving(true)
    try {
      const newEquipment: CustomEquipment = {
        id: `equipment-${Date.now()}`,
        name: data.name,
        type: data.type,
        description: data.description,
        brand: data.brand,
        model: data.model,
        purchaseUrl: data.purchaseUrl,
        imageUrl: data.imageUrl,
        tags: data.tags,
        createdBy: 'current-coach', // This should come from auth context
        isCustom: true,
        createdAt: new Date(),
        updatedAt: new Date()
      }

      addEquipment(newEquipment)
      toast.success('Equipment added successfully!')
      handleClose()
    } catch (error) {
      console.error('Failed to create equipment:', error)
      toast.error('Failed to add equipment')
    } finally {
      setSaving(false)
    }
  }

  if (!isCreateEquipmentModalOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Add New Equipment</h2>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Name *
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., TRX Suspension Trainer"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Type *
                </label>
                <select
                  {...register('type')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {equipmentTypeOptions.map(type => (
                    <option key={type} value={type}>
                      {type.replace('-', ' ').charAt(0).toUpperCase() + type.replace('-', ' ').slice(1)}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  {...register('description')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the equipment, its uses, and any special features..."
                />
              </div>
            </div>

            {/* Product Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Building className="w-5 h-5 mr-2" />
                Product Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand
                  </label>
                  <input
                    type="text"
                    {...register('brand')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., TRX, Concept2, Rogue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <input
                    type="text"
                    {...register('model')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., TRX Pro, Model D"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purchase URL
                </label>
                <div className="relative">
                  <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="url"
                    {...register('purchaseUrl')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/product"
                  />
                </div>
                {errors.purchaseUrl && (
                  <p className="mt-1 text-sm text-red-600">{errors.purchaseUrl.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image URL
                </label>
                <div className="relative">
                  <Upload className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="url"
                    {...register('imageUrl')}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                {errors.imageUrl && (
                  <p className="mt-1 text-sm text-red-600">{errors.imageUrl.message}</p>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <p className="text-sm text-gray-500 mb-2">
                Add tags to help categorize and find this equipment easily
              </p>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., cardio, strength, portable..."
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <span
                        key={tag}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-blue-500 hover:text-blue-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Suggested Tags */}
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Suggested tags:</p>
                <div className="flex flex-wrap gap-1">
                  {['strength', 'cardio', 'functional', 'portable', 'heavy-duty', 'beginner-friendly'].map(suggestedTag => (
                    <button
                      key={suggestedTag}
                      type="button"
                      onClick={() => {
                        if (!tags.includes(suggestedTag)) {
                          const updatedTags = [...tags, suggestedTag]
                          setTags(updatedTags)
                          setValue('tags', updatedTags)
                        }
                      }}
                      className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                    >
                      + {suggestedTag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
            className="coach-button-primary px-6 py-2 flex items-center disabled:opacity-50"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Adding...</span>
              </>
            ) : (
              <>
                <Package className="w-4 h-4 mr-2" />
                Add Equipment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}